import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { DegreeProvider } from './context/DegreeContext';
import PrivateRoute from './components/PrivateRoute';
import PublicRoute from './components/PublicRoute';
import MainLayout from './components/Layout/MainLayout';
import ErrorBoundary from './components/ErrorBoundary';

// Auth Pages
import Login from './pages/Login';
import Register from './pages/Register';
import EmployerRegistration from './pages/Recruiter/EmployerRegistration';
import AccessDenied from './pages/AccessDenied';

// Main Pages
import Dashboard from './pages/Dashboard';




import OnboardCollege from './pages/Colleges/OnboardCollege';
import AcademicCalendarPage from './pages/Colleges/AcademicCalendarPage';

import CollegeProfile from './pages/Colleges/CollegeProfile';
import CollegeManagement from './pages/Colleges/CollegeManagement';
import ManageBatches from './pages/Colleges/ManageBatches';

import UserManagement from './pages/Users/UserManagement';
import UserDetails from './pages/Users/UserDetails';
import EditUser from './pages/Users/EditUser';
import CreateUser from './pages/Users/CreateUser';

import ManageCourses from './pages/Courses/ManageCourses';
import CourseCreation from './pages/Courses/CourseCreation';
import CourseBuilder from './pages/Courses/CourseBuilder';
import ModuleContentBuilder from './pages/Courses/ModuleContentBuilder';
import LearningFlowEditor from './pages/Courses/LearningFlowEditor';
import AssessmentBuilder from './pages/Courses/AssessmentBuilder';

import CreateAssessment from './pages/Assessments/CreateAssessment';
import AssessmentListView from './pages/Assessments/AssessmentListView';
import AssessmentView from './pages/Assessments/AssessmentView';
import AssessmentSubmissions from './pages/Assessments/AssessmentSubmissions';
import Analytics from './pages/Analytics/Analytics';
import AnalyticsDashboard from './pages/Analytics/AnalyticsDashboard';
// eslint-disable-next-line no-unused-vars
import TicketList from './pages/Support/TicketList';
import TicketDetail from './pages/Support/TicketDetail';
import MyITSupport from './pages/Support/MyITSupport';
import ITSupportManagement from './pages/Support/ITSupportManagement';
import InstituteChainManagement from './pages/Colleges/InstituteChainManagement';
import InstituteChainDetails from './pages/Colleges/InstituteChainDetails';
import Profile from './pages/Profile';
import Settings from './pages/College/Settings';
import CaseLogList from './pages/Admin/CaseLogList';
import CommunityHub from './pages/Admin/CommunityHub';
import ActivityLog from './pages/Admin/ActivityLog';
import AccessLogs from './pages/Admin/AccessLogs';
import Proctor from './pages/Admin/Proctor';


// Components


// Student Pages
import CourseCatalog from './pages/Courses/CourseCatalog';
import MyCourses from './pages/Student/MyCourses';
import CoursePlayer from './pages/Student/CoursePlayer';
import StageGate from './pages/Student/StageGate';
import SkillsPassport from './pages/Student/SkillsPassport';
import MicroAssessmentList from './pages/Student/MicroAssessmentList';
import MicroAssessmentPlayer from './pages/Student/MicroAssessmentPlayer';
import StudentJobFairs from './pages/Student/StudentJobFairs';


// College Admin Pages
import AddStudent from './pages/College/AddStudent';
import BulkUploadStudents from './pages/College/BulkUploadStudents';
import StudentManagement from './pages/College/StudentManagement';
import Notifications from './pages/College/Notifications';
import Reports from './pages/College/Reports';

import DepartmentManagement from './pages/College/DepartmentManagement';
import StudentView from './pages/College/StudentView';
import CollegeDegrees from './pages/College/CollegeDegrees';
import PlacementOffice from './pages/College/PlacementOffice';
import CollegeRecruiter from './pages/College/CollegeRecruiter';
import ActiveSessions from './pages/Security/ActiveSessions';
import StudentProgress from './pages/College/StudentProgress';
import CareerAgentAnalytics from './pages/College/CareerAgentAnalytics';
import NotFound from './pages/NotFound';
import EmailTester from './pages/Admin/EmailTester';
import UploadCGPA from './pages/College/UploadCGPA';

// Role-specific Dashboards (Lazy Loaded)
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const StudentOnboarding = lazy(() => import('./pages/Admin/StudentOnboarding'));
const OnboardStudentPage = lazy(() => import('./pages/Admin/OnboardStudentPage'));
const AddRecruiter = lazy(() => import('./pages/Admin/AddRecruiter'));
const RecruiterManagement = lazy(() => import('./pages/Admin/RecruiterManagement'));
const JobFairApplications = lazy(() => import('./pages/Admin/JobFairApplications'));
const JobApplicants = lazy(() => import('./pages/Admin/JobApplicants'));
const RecruiterFlow = lazy(() => import('./pages/Admin/RecruiterFlow'));
const RecruiterWorkflow = lazy(() => import('./pages/Admin/RecruiterWorkflow'));
const GrievanceList = lazy(() => import('./pages/Admin/GrievanceList'));
const CollegeDashboard = lazy(() => import('./pages/Dashboards/CollegeDashboard'));
const StudentDashboard = lazy(() => import('./pages/Dashboards/StudentDashboard'));
const RecruiterDashboard = lazy(() => import('./pages/Dashboards/RecruiterDashboard'));
const RecruiterJobs = lazy(() => import('./pages/Recruiter/JobManagement'));
const RecruiterApplicants = lazy(() => import('./pages/Recruiter/Applicants'));
const RecruiterApplicantDetails = lazy(() => import('./pages/Recruiter/ApplicantDetails'));
const RecruiterJobFairs = lazy(() => import('./pages/Recruiter/RecruiterJobFairs'));
const RecruiterJobFairDetails = lazy(() => import('./pages/Recruiter/RecruiterJobFairDetails'));

const App = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <DegreeProvider>
          <Router>
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 3000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#10b981',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 4000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />

            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a3884]"></div>
              </div>
            }>
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                } />
                <Route path="/register" element={
                  <PublicRoute>
                    <Register />
                  </PublicRoute>
                } />
                <Route path="/employer/register" element={
                  <PublicRoute>
                    <EmployerRegistration />
                  </PublicRoute>
                } />

                {/* Protected Routes */}
                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <MainLayout />
                    </PrivateRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="notifications" element={<Notifications />} />

                  {/* Role-specific Dashboards */}
                  <Route
                    path="admin/dashboard"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <AdminDashboard />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/dashboard-new"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <AdminDashboard />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/student-onboarding"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin', 'college_admin']}>
                        <StudentOnboarding />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/student-onboarding/add"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin', 'college_admin']}>
                        <OnboardStudentPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/add-recruiter"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <AddRecruiter />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/edit-recruiter/:id"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <AddRecruiter />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/recruiters"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <RecruiterManagement />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/job-fairs/:id/applications"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <JobFairApplications />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/jobs/:jobId/applicants"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <JobApplicants />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/recruiter-flow"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin', 'college_admin', 'recruiter']}>
                        <RecruiterFlow />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/recruiter-workflow"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin', 'college_admin', 'recruiter']}>
                        <RecruiterWorkflow />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/student-onboarding/edit/:id"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin', 'college_admin']}>
                        <OnboardStudentPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/email-tester"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <EmailTester />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/notifications"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <Notifications />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/career-analytics"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <CareerAgentAnalytics />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/proctor"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <Proctor />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/grievances"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <GrievanceList />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/community"
                    element={
                      <PrivateRoute roles={['admin', 'college_admin', 'office_admin']}>
                        <CommunityHub />
                      </PrivateRoute>
                    }
                  />



                  <Route
                    path="college/dashboard"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <CollegeDashboard />
                      </PrivateRoute>
                    }
                  />

                  {/* College Admin Routes */}
                  <Route
                    path="college/departments"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <DepartmentManagement />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="college/degrees"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <CollegeDegrees />
                      </PrivateRoute>
                    }
                  />



                  <Route
                    path="college/students"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <StudentManagement />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="college/placement"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <PlacementOffice />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="college/recruiter"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <CollegeRecruiter />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="college/add-recruiter"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <AddRecruiter fromCollege={true} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="college/edit-recruiter/:id"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <AddRecruiter fromCollege={true} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="college/career-analytics"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <CareerAgentAnalytics />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="college/courses"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <ManageCourses />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="college/analytics"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <Analytics />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="college/calendar"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <AcademicCalendarPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="college/reports"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <Reports />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="college/notifications"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <Notifications />
                      </PrivateRoute>
                    }
                  />




                  <Route
                    path="student/dashboard"
                    element={
                      <PrivateRoute roles={['student']}>
                        <StudentDashboard />
                      </PrivateRoute>
                    }
                  />


                  <Route
                    path="recruiter/dashboard"
                    element={
                      <PrivateRoute roles={['recruiter']}>
                        <RecruiterDashboard />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="recruiter/jobs"
                    element={
                      <PrivateRoute roles={['recruiter']}>
                        <RecruiterJobs />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="recruiter/applicants"
                    element={
                      <PrivateRoute roles={['recruiter']}>
                        <RecruiterApplicants />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="recruiter/applicants/:id"
                    element={
                      <PrivateRoute roles={['recruiter']}>
                        <RecruiterApplicantDetails />
                      </PrivateRoute>
                    }
                  />

                  {/* Recruiter Job Fairs */}
                  <Route
                    path="recruiter/job-fairs"
                    element={
                      <PrivateRoute roles={['recruiter']}>
                        <RecruiterJobFairs />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="recruiter/job-fairs/:id"
                    element={
                      <PrivateRoute roles={['recruiter']}>
                        <RecruiterJobFairDetails />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="colleges"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <CollegeManagement />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="colleges/onboard"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <OnboardCollege />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="colleges/edit/:id"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <OnboardCollege />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="college/job-fairs/:id"
                    element={
                      <PrivateRoute roles={['admin', 'college_admin', 'office_admin']}>
                        <RecruiterJobFairDetails />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="manage-batches"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin', 'college_admin']}>
                        <ManageBatches />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="colleges/:id"
                    element={
                      <PrivateRoute roles={['admin', 'college_admin', 'office_admin']}>
                        <CollegeProfile />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="users"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <UserManagement />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="users/edit/:id"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <EditUser />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="users/:id"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <UserDetails />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="users/create"
                    element={
                      <PrivateRoute roles={['admin', 'college_admin']}>
                        <CreateUser />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="courses"
                    element={
                      <PrivateRoute roles={['admin', 'college_admin', 'office_admin']}>
                        <ManageCourses />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="courses/create"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <CourseCreation />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="courses/edit/:id"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <CourseCreation />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="courses/:id/builder"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <CourseBuilder />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="courses/:courseId/modules/:moduleId/content"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <ModuleContentBuilder />
                      </PrivateRoute>
                    }
                  />

                  {/* SMAART 7-Stage Learning Flow Editor */}
                  <Route
                    path="courses/:id/learning-flow"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <LearningFlowEditor />
                      </PrivateRoute>
                    }
                  />

                  {/* SMAART MCQ + FIB + SJT Assessment Builder */}
                  <Route
                    path="courses/:id/assessment-builder"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <AssessmentBuilder />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="assessments"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <AssessmentListView />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="assessments/create"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <CreateAssessment />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="assessments/view/:id"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <AssessmentView />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="assessments/edit/:id"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <CreateAssessment />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="assessments/:id/submissions"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <AssessmentSubmissions />
                      </PrivateRoute>
                    }
                  />





                  <Route
                    path="analytics"
                    element={
                      <PrivateRoute roles={['admin', 'college_admin']}>
                        <Analytics />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="analytics/dashboard"
                    element={
                      <PrivateRoute roles={['admin', 'college_admin']}>
                        <AnalyticsDashboard />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="it-support"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <ITSupportManagement />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="institute-chain"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <InstituteChainManagement />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="institute-chain/:id"
                    element={
                      <PrivateRoute roles={['admin', 'office_admin']}>
                        <InstituteChainDetails />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="support/dashboard"
                    element={
                      <PrivateRoute roles={['admin', 'support', 'office_admin']}>
                        <TicketList />
                      </PrivateRoute>
                    }
                  />

                  <Route path="support" element={<Navigate to="/support/dashboard" replace />} />

                  <Route
                    path="support/:id"
                    element={
                      <PrivateRoute roles={['admin', 'support', 'office_admin']}>
                        <TicketDetail />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="support/tickets/:id"
                    element={
                      <PrivateRoute roles={['admin', 'support', 'office_admin']}>
                        <TicketDetail />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="my-it-support"
                    element={
                      <PrivateRoute roles={['admin', 'support', 'college_admin', 'office_admin']}>
                        <MyITSupport />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="case-log"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <CaseLogList />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/activity-log"
                    element={
                      <PrivateRoute roles={['admin', 'college_admin']}>
                        <ActivityLog />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="admin/access-logs"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <AccessLogs />
                      </PrivateRoute>
                    }
                  />

                  {/* Security Routes */}
                  <Route
                    path="security/sessions"
                    element={
                      <PrivateRoute>
                        <ActiveSessions />
                      </PrivateRoute>
                    }
                  />

                  {/* College Admin Routes */}


                  <Route
                    path="college/students/add"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <AddStudent />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="college/students/bulk-upload"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <BulkUploadStudents />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="college/students/:id"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <StudentView />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="college/upload-cgpa"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <UploadCGPA />
                      </PrivateRoute>
                    }
                  />

                  {/* College Admin — Student Progress */}
                  <Route
                    path="college/student-progress"
                    element={
                      <PrivateRoute roles={['college_admin', 'admin']}>
                        <StudentProgress />
                      </PrivateRoute>
                    }
                  />



                  <Route
                    path="college/notifications"
                    element={
                      <PrivateRoute roles={['college_admin']}>
                        <Notifications />
                      </PrivateRoute>
                    }
                  />

                  {/* New College Management Routes */}


                  {/* Student Routes */}
                  <Route
                    path="student/courses"
                    element={
                      <PrivateRoute roles={['student']}>
                        <CourseCatalog />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="student/my-courses"
                    element={
                      <PrivateRoute roles={['student']}>
                        <MyCourses />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="student/courses/:id/player"
                    element={
                      <PrivateRoute roles={['student']}>
                        <CoursePlayer />
                      </PrivateRoute>
                    }
                  />

                  {/* SMAART Gate Assessments (T1–T4) */}
                  <Route
                    path="student/gate/:gateCode"
                    element={
                      <PrivateRoute roles={['student']}>
                        <StageGate />
                      </PrivateRoute>
                    }
                  />

                  {/* Skills Passport */}
                  <Route
                    path="student/passport"
                    element={
                      <PrivateRoute roles={['student']}>
                        <SkillsPassport />
                      </PrivateRoute>
                    }
                  />

                  {/* Student Job Fairs */}
                  <Route
                    path="student/job-fairs"
                    element={
                      <PrivateRoute roles={['student']}>
                        <StudentJobFairs />
                      </PrivateRoute>
                    }
                  />

                  {/* Micro Assessments */}
                  <Route
                    path="student/micro-assessments"
                    element={
                      <PrivateRoute roles={['student']}>
                        <MicroAssessmentList />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="student/micro-assessments/:id"
                    element={
                      <PrivateRoute roles={['student']}>
                        <MicroAssessmentPlayer />
                      </PrivateRoute>
                    }
                  />
                </Route>

                {/* Access Denied */}
                <Route path="/access-denied" element={<AccessDenied />} />
                <Route path="/unauthorized" element={<Navigate to="/access-denied" replace />} />

                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Router>
          </DegreeProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
// Trigger recompile
